'use client';;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:tillamook-com":true});}catch(e){}}();

import React, { useCallback, useEffect, useMemo, useState } from 'react';

import cx from 'classnames';
import { usePathname } from 'next/navigation';
import PropTypes from 'prop-types';

import Lockup from 'components/blocks/shared/Lockup';
import SharpnessStory from 'components/overlays/SharpnessStory';
import SharpnessCloseButton from 'components/overlays/SharpnessStory/SharpnessCloseButton';
import ButtonCTA from 'components/ui/ButtonCTA';
import FlavorProfileGraph from 'components/ui/FlavorProfileGraph';
import Portal from 'components/ui/Portal';

import { useTheme } from 'hooks/useTheme';
import { getColor } from 'utils';
import { checkColorContrast } from 'utils/colors';

import styles from './BlockProductSharpnessStory.module.scss';

let closeRoute = false;

const eyebrowOverrides = {
    'makers-reserve': {
        baseTheme: 'labelSmallAlt',
        themes: { medium: 'labelMediumAlt' },
    },
};

const headlineOverrides = {
    'makers-reserve': {
        baseTheme: 'makers48',
        themes: { medium: 'makers60' },
    },
};

const eyebrowColorMap = {
    'makers-reserve': {
        eyebrowColor: 'makersReserveGoldAlt',
        headlineColor: 'cream',
    },
    default: {
        eyebrowColor: 'cream',
        headlineColor: 'cream',
    },
    defaultDark: {
        eyebrowColor: 'blue',
        headlineColor: 'blue',
    },
};

const BlockProductSharpnessStory = ({
    icon,
    eyebrow = '',
    headline,
    copy,
    ctaText,
    sharpnessStory,
    sharpnessStoryItem,
    sharpnessStoryPage,
    flushTop,
    flushBottom,
    backgroundColor,
    masthead,
}) => {
    const [isOpen, setOpen] = useState(false);
    const [isReveal, setIsReveal] = useState(false);
    const [globalTheme] = useTheme();
    const pathname = usePathname();

    const isDark = useMemo(
        () => checkColorContrast(backgroundColor || '') < 2,
        [backgroundColor]
    );

    const theme = globalTheme;

    const eyebrowColor = isDark
        ? eyebrowColorMap.defaultDark.eyebrowColor
        : eyebrowColorMap[theme]
          ? eyebrowColorMap[theme].eyebrowColor
          : eyebrowColorMap.default.eyebrowColor;

    const headlineColor = isDark
        ? eyebrowColorMap.defaultDark.headlineColor
        : eyebrowColorMap[theme]
          ? eyebrowColorMap[theme].headlineColor
          : eyebrowColorMap.default.headlineColor;

    const handleOpen = () => {
        closeRoute = pathname || false;
        window.history.pushState({}, '', `/${sharpnessStoryPage.slug}`);
        setOpen(true);

        setTimeout(() => {
            window.requestAnimationFrame(() => {
                setIsReveal(true);
            });
        }, 0);
    };

    const handleClose = useCallback(() => {
        const targetRoute = closeRoute
            ? closeRoute.toString()
            : `/${sharpnessStory.closeLink.slug}`;
        window.history.pushState({}, '', targetRoute);
        setIsReveal(false);
    }, [sharpnessStory.closeLink.slug]);

    const handleKeyDown = useCallback(
        event => {
            if (event.code === 'Escape') {
                handleClose();
            }
        },
        [handleClose]
    );

    const onCloseTransitionEnd = e => {
        if (e.propertyName === 'transform' && !isReveal) setOpen(false);
    };

    useEffect(() => {
        document.addEventListener('keydown', handleKeyDown, false);
        return () => {
            document.removeEventListener('keydown', handleKeyDown, false);
        };
    }, [handleKeyDown]);

    return (
        <article
            className={cx(styles.root, [styles[`theme--${theme}`]], {
                [styles.flushTop]: flushTop,
                [styles.flushBottom]: flushBottom,
                [styles.isDark]: isDark,
            })}
            style={{
                '--background-color': getColor(backgroundColor || ''),
                '--foreground-color': getColor(
                    sharpnessStoryItem?.foregroundColor || 'blue'
                ),
            }}
        >
            <div className={styles.inner}>
                <div className={styles.innerAspect}>
                    <div className={styles.aspectBox}>
                        <FlavorProfileGraph
                            foregroundColor={
                                sharpnessStoryItem?.foregroundColor || 'blue'
                            }
                            values={sharpnessStoryItem?.flavorProfileData}
                        />
                    </div>
                </div>
                <div className={styles.innerContent}>
                    <div
                        className={styles.block}
                        style={{
                            color: `var(--colors-${headlineColor || 'cream'})`,
                        }}
                    >
                        <div
                            className={styles.content}
                            style={{
                                '--lockup-eyebrow-color': `var(--colors-${eyebrowColor})`,
                                '--lockup-headline-color': `var(--colors-${
                                    headlineColor || 'blue'
                                })`,
                            }}
                        >
                            <Lockup
                                icon={icon}
                                eyebrow={{
                                    text: eyebrow,
                                    themeOverrides: theme
                                        ? eyebrowOverrides[theme]
                                        : undefined,
                                }}
                                headline={{
                                    text: headline,
                                    themeOverrides: theme
                                        ? headlineOverrides[theme]
                                        : undefined,
                                }}
                                copy={{
                                    text: copy,
                                }}
                                ctaSlot={
                                    ctaText && (
                                        <ButtonCTA
                                            className={styles.cta}
                                            text={ctaText}
                                            style={
                                                theme === 'makers-reserve'
                                                    ? 'makersFilled-gold-cream'
                                                    : 'filled-blue-cream'
                                            }
                                            onClick={handleOpen}
                                        />
                                    )
                                }
                                theme="Small"
                            />
                        </div>
                    </div>
                    {isOpen && (
                        <Portal>
                            <div
                                className={cx(styles.overlay, {
                                    [styles.isReveal]: isReveal,
                                })}
                                onTransitionEnd={onCloseTransitionEnd}
                            >
                                <SharpnessStory
                                    {...sharpnessStory}
                                    masthead={masthead}
                                    items={sharpnessStory.itemsCollection.items}
                                />
                            </div>
                            <SharpnessCloseButton
                                onClose={handleClose}
                                className={cx(styles.overlayCloseButton, {
                                    [styles.isReveal]: isReveal,
                                })}
                            />
                        </Portal>
                    )}
                </div>
            </div>
        </article>
    );
};

BlockProductSharpnessStory.propTypes = {
    backgroundColor: PropTypes.string,
    eyebrow: PropTypes.string,
    headline: PropTypes.object,
    copy: PropTypes.object,
    sharpnessStory: PropTypes.object,
    ctaText: PropTypes.string,
    flushTop: PropTypes.bool,
    flushBottom: PropTypes.bool,
    sharpnessStoryItem: PropTypes.shape({
        flavorProfileData: PropTypes.array.isRequired,
    }),
    sharpnessStoryPage: PropTypes.shape({ slug: PropTypes.string.isRequired }),
    image: PropTypes.shape({
        description: PropTypes.string,
        url: PropTypes.string,
    }),
    imageLarge: PropTypes.shape({
        description: PropTypes.string,
        url: PropTypes.string,
    }),
    masthead: PropTypes.object,
};

export default BlockProductSharpnessStory;
